<template>
  <div :style="{ maxHeight }" class="flex flex-col w-[266px]">
    <div
      v-if="showSearch"
      class="flex flex-col py-3 px-2 border_bottom flex-shrink-0"
    >
      <span
        v-if="searchLabel"
        class="secondary_color proxima_nova font-semibold text-xs leading-[17px] px-3 pb-2"
      >
        {{ searchLabel }}
      </span>

      <a-input
        v-model:value="searchQuery"
        :placeholder="searchPlaceholder"
        class="input_medium"
        autofocus
      >
        <template #prefix>
          <ThemeImage
            class="size-4"
            :icon="imageNames.SEARCH_VARIANT1"
            alt="search_icon"
          />
        </template>
      </a-input>
    </div>

    <div class="dropdown_options_scroll flex flex-col custom_scrollbar">
      <div v-if="filteredOptions?.length" class="flex flex-col p-2">
        <template
          v-for="(option, index) in filteredOptions"
          :key="option.id || option.value || index"
        >
          <div
            class="dropdown_item flex-shrink-0 flex items-center flex-row gap-3 rounded-lg"
            :class="{ dropdown_item_selected: isSelected(option) }"
            @click="handleSelect(option)"
          >
            <ThemeImage
              v-if="isSelected(option)"
              class="size-4 shrink-0"
              :icon="imageNames.TICK_GREY"
              alt="check_icon"
            />
            <div v-else class="size-4 shrink-0" />

            <ProfileIcon
              v-if="showAvatar"
              :data="getAvatarData(option)"
              :font="12"
              :size="28"
              :border-radius="50"
            />

            <span class="text_color text-sm font-normal leading-[17px]">
              {{ getOptionLabel(option) }}
            </span>
          </div>

          <div
            v-if="selectedCount > 0 && index === selectedCount - 1 && selectedCount < filteredOptions.length"
            class="selected_separator"
          ></div>
        </template>
      </div>

      <div v-else class="flex flex-col">
        <EmptyScreen
          source="dropdown_no_results"
          :text="noResultsText"
          type="search"
          css="size-12"
          padding="py-8"
        />
      </div>
    </div>

    <div
      v-if="showUnselectAll && selectedValues?.length > 0"
      class="flex flex-row justify-end px-4 py-3 border_top flex-shrink-0"
    >
      <button
        class="unselect_all_button proxima_nova text-xs"
        @click="handleUnselectAll"
      >
        Unselect All
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { sortOptionsSelectedFirst } from "@/src/utils/dropdownUtils.js";

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  selectedValues: {
    type: Array,
    default: () => [],
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  searchLabel: {
    type: String,
    default: "",
  },
  searchPlaceholder: {
    type: String,
    default: "Search...",
  },
  showAvatar: {
    type: Boolean,
    default: false,
  },
  showUnselectAll: {
    type: Boolean,
    default: true,
  },
  noResultsText: {
    type: String,
    default: "No results found",
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: String,
    default: "380px",
  },
});

const emit = defineEmits(["select", "unselect-all"]);

const store = useStore();
const storeUsers = computed(() => store.getters["users/users"] || []);

const getAvatarData = (option) => {
  if (!props.showAvatar) return option;
  if (option?.avatar_color) return option;
  if (option?.id) {
    const found = storeUsers.value.find((u) => u.id === option.id);
    if (found) return found;
  }
  if (option?.name) {
    const lowerName = option.name.toLowerCase();
    const found = storeUsers.value.find((u) => u.name?.toLowerCase() === lowerName);
    if (found) return found;
  }
  return option;
};

const searchQuery = ref("");

const baseFilteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options;
  }
  const searchTerm = searchQuery.value.toLowerCase().trim();
  return props.options.filter((option) => {
    const label = getOptionLabel(option);
    return label.toLowerCase().includes(searchTerm);
  });
});

const sortedOptionsData = computed(() => {
  const options = baseFilteredOptions.value;
  if (!props.selectedValues || props.selectedValues.length === 0 || !options || options.length === 0) {
    return { sortedOptions: options || [], selectedCount: 0 };
  }
  return sortOptionsSelectedFirst(options, isSelected);
});

const filteredOptions = computed(() => sortedOptionsData.value.sortedOptions);

const selectedCount = computed(() => sortedOptionsData.value.selectedCount);

const getOptionLabel = (option) => {
  if (!option) return "";
  if (typeof option === "string") return option;
  if (typeof option === "number") return String(option);
  return option.label || option.name || option.value || "";
};

const getOptionValue = (option) => {
  if (!option) return "";
  if (typeof option === "string") return option;
  if (typeof option === "number") return option;
  return option.value || option.id || option.label || option.name || "";
};

const isSelected = (option) => {
  if (!props.selectedValues || !Array.isArray(props.selectedValues))
    return false;

  const optionValue = getOptionValue(option);

  return props.selectedValues.some((selected) => {
    if (!selected) return false;

    const selectedValue =
      typeof selected === "object" ? getOptionValue(selected) : selected;

    return String(selectedValue) === String(optionValue);
  });
};

const handleSelect = (option) => {
  emit("select", option);
  if (!props.multiple) {
    searchQuery.value = "";
  }
};

const handleUnselectAll = () => {
  emit("unselect-all");
};
</script>

<style scoped>
.dropdown_options_scroll {
  min-height: 80px;
  overflow-y: auto;
  flex: 1;
}

.dropdown_item_selected {
  background-color: var(--white-opacity-10);
}

.unselect_all_button {
  color: var(--text-color);
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.unselect_all_button:hover {
  opacity: 0.7;
}

.selected_separator {
  height: 1px;
  background-color: var(--white-opacity-8);
  margin: 4px 12px;
  flex-shrink: 0;
}
</style>
